import { LoadingSpinner } from './LoadingSpinner';
import { cn } from '@/lib/utils';

interface PageLoaderProps {
  message?: string;
  className?: string;
  fullScreen?: boolean;
}

export function PageLoader({ 
  message = 'Loading...', 
  className,
  fullScreen = false 
}: PageLoaderProps) {
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-linen backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="font-body text-base text-night-700">{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex flex-col items-center justify-center min-h-[400px] space-y-6',
      className
    )}>
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-lilac-900 rounded-full flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-night-900 mb-2">
            {message}
          </h3>
          <p className="font-body text-night-500">
            Please wait while we load your content.
          </p>
        </div>
      </div>
    </div>
  );
}

interface PageLoadingStateProps {
  title?: string;
  description?: string;
  className?: string;
}

export function PageLoadingState({ 
  title = 'Loading...', 
  description = 'Please wait while we fetch your data.',
  className 
}: PageLoadingStateProps) {
  return (
    <div className={cn('p-8 text-center bg-white', className)}>
      <div className="space-y-4 max-w-md mx-auto">
        <div className="mx-auto w-16 h-16 bg-blush-50 rounded-full flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
            {title}
          </h3>
          <p className="font-body text-gray-600">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

interface SectionLoadingStateProps {
  className?: string;
}

export function SectionLoadingState({ className }: SectionLoadingStateProps) {
  return (
    <div className={cn('flex items-center justify-center py-12', className)}>
      <div className="text-center space-y-3">
        <LoadingSpinner size="md" />
        <p className="font-body text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  );
}